/**
 * Catalog of orderable Zazzle products.
 *
 * To add a new product:
 *   1. Look up the Zazzle product id from the product URL
 *      (e.g. https://www.zazzle.com/geoshaped_button-256075812325269155 → 256075812325269155).
 *   2. Drop a preview image into src/images/products/ — the basename must
 *      match `imageFile` below (e.g. button.jpg). Missing files render a
 *      coloured placeholder instead of breaking the build.
 *   3. Add an entry to PRODUCTS.
 */

// Auto-discover any image file in src/images/products/.
const productImagesCtx = require.context(
    './images/products',
    false,
    /\.(jpe?g|png|webp|gif|svg)$/
);
const productImageMap = productImagesCtx.keys().reduce((acc, key) => {
    const fileName = key.replace(/^\.\//, '');
    acc[fileName] = productImagesCtx(key);
    return acc;
}, {});

const resolveImage = (fileName) => productImageMap[fileName];

export const PRODUCTS = [
    {
        id: 'cup',
        name: 'Cup',
        zazzleProductId: '168985417233000053',
        supportsLabel: true,
        imageFile: 'cup.jpg',
        analyticsKey: 'cup',
        accent: '#8B4513',
        // Cylindrical wrap: design is sine-projected onto the cup's virtual
        // cylinder. Every knob below is independently tunable — feel free to
        // dial each one without worrying about hidden math.
        //
        //   bounds       — rectangle on cup-empty.jpg where the wrap CANVAS
        //                  sits, as % of base. Wrap region is placed inside
        //                  this by anchor + scale; outside is transparent.
        //   coverageDeg  — angular range of cylinder the design covers.
        //                  180° = design exactly fills the visible front.
        //                  >180° = design's edges wrap PAST the visible front
        //                          (cut off on the sides, "map goes beyond").
        //                  <180° = design only covers part of the front
        //                          (cup body visible on left/right of design).
        //   offsetDeg    — angular rotation of the source on the cylinder.
        //                  Negative shifts source center to the right on
        //                  screen; positive shifts left.
        //   radiusPct    — cylinder radius as a fraction of the canvas width.
        //                  The visible front-face width on screen = 2 *
        //                  radiusPct (so 0.5 = wrap fills full canvas width).
        //                  Controls how FAT the cup body face appears.
        //                  null (default) → derive from source aspect.
        //   bandHeightPct — height of the print band as a fraction of the
        //                  canvas height. Controls how TALL the design area
        //                  is on the cup. null (default) → derive from
        //                  source aspect so it doesn't squish.
        //   tiltTopDeg /
        //   tiltBottomDeg — camera tilt for the TOP and BOTTOM edges of the
        //                   print band, independently. Positive = sides
        //                   higher than center (cup viewed from above, smile
        //                   curve). 0 = flat. Top and bottom usually differ
        //                   on real cups — the rim and base have different
        //                   ellipse curvatures. `tiltDeg` (shorthand) sets
        //                   both at once.
        //   fit          — 'contain' (default): wrap region sized by source
        //                  aspect / coverage; 'fill': wrap stretches to bounds.
        //   anchorX /
        //   anchorY      — where the wrap sits inside bounds when fit='contain'.
        //                  -1 = top/left, 0 = center (default), +1 = bottom/right.
        //   widthScale /
        //   heightScale  — multiply the wrap region's natural size. Useful for
        //                  shrinking the design without changing bounds.
        //   xShift /
        //   yShift       — translate wrap inside bounds as a fraction of bounds.
        //   sourceCrop   — crop source BEFORE wrap. Useful if source has white
        //                  margin you don't want sine-stretched onto the edges.
        //                  { top, bottom, left, right } in [0, 1).
        //
        // Zazzle's cup template has no separate text field — the exported
        // design PNG already bakes in the "X/Y states" pill — so no caption
        // layer here.
        livePreview: {
            baseFile: 'cup-empty.jpg',
            design: { x: 35.86, y: 32.0, w: 36.00, h: 52.0 },
            designCaptioned: { x: 32.86, y: 32.0, w: 38.00, h: 52.0 },
            wrap: {
                type: 'cylindrical',
                coverageDeg: 188,
                offsetDeg: -34,
                radiusPct: 0.6,   // null = auto from source aspect; try ~0.45 to set explicitly
                bandHeightPct: 0.88,   // null = auto from source aspect; try ~0.85 to set explicitly
                tiltTopDeg: 8,
                tiltBottomDeg: 11,
                fit: 'contain',
                anchorX: -0,
                anchorY: 0,
                widthScale: 0.91,
                heightScale: 0.96,
                xShift: -0.053,
                yShift: -0.02,
                sourceCrop: { top: 0, bottom: 0.03, left: 0, right: 0 },
            },
        },
    },
    {
        id: 'tshirt',
        name: 'T-Shirt',
        zazzleProductId: '256170833782451781',
        supportsLabel: true,
        imageFile: 'tshirt.jpg',
        analyticsKey: 'tshirt',
        accent: '#2980b9',
        // LivePreview uses a flat-laid Zazzle t-shirt photo so the
        // design composites onto a neutral white tee instead of a
        // model's chest. Bounds put the print area on the upper-chest
        // region where Zazzle actually prints.
        livePreview: {
            baseFile: 'tshirt-empty.jpg',
            designFit: 'contain',
            design: { x: 31.82, y: 26.47, w: 38.96, h: 32.79 },
            designCaptioned: { x: 31.82, y: 26.47, w: 38.96, h: 32.79 },
            caption: {
                x: 51.11, y: 60.92,
                fontSize: 4.59,
                color: '#000',
                fontWeight: 400,
                maxWidth: 50
            }
        }
    },
    {
        id: 'water-bottle',
        name: 'Water Bottle',
        zazzleProductId: '256535817447462607',
        supportsLabel: true,
        imageFile: 'water-bottle.jpg',
        analyticsKey: 'water_bottle',
        accent: '#16a085',
        // Cylindrical wrap, same approach as the cup. The bottle body is much
        // narrower (~14% of base width) and taller (~65% of base height) than
        // the cup, so coverage needs to be higher (more horizontal
        // compression) and tilt smaller (bottle is viewed nearly head-on,
        // not from above). Unlike the cup, there's no handle, so the design
        // sits roughly centered (offset is small).
        //
        // Bottle template DOES have a separate text field — "X US states
        // visited" appears below the map on the bottle's white surface — so
        // we render a caption layer like the magnet/tshirt do.
        livePreview: {
            baseFile: 'water-bottle-empty.jpg',
            design: { x: 38.0, y: 42, w: 25.0, h: 26.0 },
            designCaptioned: { x: 38.0, y: 42, w: 25.0, h: 26.0 },
            caption: {
                x: 50, y: 76.4,
                fontSize: 4,
                color: '#000',
                fontWeight: 300,
                fontFamily: 'DongleApp, sans-serif',
                maxWidth: 28,
                tiltDeg: 15,
                coverageDeg: 90,
            },
            wrap: {
                type: 'cylindrical',
                coverageDeg: 180,    // narrow bottle → more compression than cup (cup=225)
                offsetDeg: 0,      // no handle → no asymmetric shift
                radiusPct: 0.6,   // auto from source aspect
                bandHeightPct: 0.92,
                tiltTopDeg: 2,      // viewed nearly head-on, small ellipse
                tiltBottomDeg: 2,
                fit: 'contain',
                anchorX: 0,
                anchorY: 0,
                widthScale: 0.8,
                heightScale: 1.0,
                xShift: 0.0,
                yShift: 0,
                sourceCrop: { top: 0, bottom: 0, left: 0, right: 0 },
            },
        },
    },
    {
        id: 'stone-magnet',
        name: 'Stone Magnet',
        zazzleProductId: '256509109583208182',
        noLabelProductId: '256720497189659463',
        supportsLabel: true,
        imageFile: 'stone-magnet.jpg',
        noLabelImageFile: 'stone-magnet-no-label.jpg',
        analyticsKey: 'stone_magnet',
        accent: '#7f8c8d',
        // Square marble stone; empty base has the stone face filling nearly
        // the entire image (~x=10-90, y=10-90). Larger printable area so
        // bounds are bigger than the round magnet.
        // PIXEL-PERFECT bounds from scripts/find-design-bounds.py.
        // Bounds measured from Zazzle's realview render via the screenshot test.
        // Stone marble surface absorbs the ink — Zazzle blends the design with
        // the underlying texture; multiply mode + reduced opacity emulates that
        // so live preview matches.
        livePreview: {
            baseFile: 'stone-magnet-empty.jpg',
            designFit: 'contain',
            designBlendMode: 'multiply',
            designOpacity: 0.85,
            design: { x: 25.85, y: 32.71, w: 48.56, h: 34.58 },
            designCaptioned: { x: 25.87, y: 29.98, w: 48.73, h: 34.00 },
            caption: {
                x: 50, y: 67.17,
                fontSize: 5.60,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'magnet',
        name: 'Magnet',
        zazzleProductId: '256797496597517231',
        noLabelProductId: '256172813999423100',
        supportsLabel: true,
        imageFile: 'magnet.jpg',
        noLabelImageFile: 'magnet-no-label.jpg',
        analyticsKey: 'magnet',
        accent: '#e67e22',
        // Live preview: composite the user's current design — and the
        // user's caption — on top of a truly empty Zazzle product base
        // (sourced from Zazzle's realview service). All bounds are in %
        // of the base image so the layout scales with the rendered card.
        //
        // `design` is used when the user has NOT entered a caption (so the
        // design fills more of the magnet's white face). `designCaptioned`
        // is used when there IS a caption — it's smaller and shifted up to
        // leave room for the caption underneath.
        livePreview: {
            baseFile: 'magnet-empty.jpg',
            // Bounds tuned to match Zazzle's actual print position from
            // magnet.jpg: gray placeholder lives at x≈12-88 y≈22-78 when
            // accounting for the padding around the SVG inside the captured
            // PNG. The visible map ends up sitting at roughly x=21-79
            // y=32-62, matching the Zazzle reference.
            // Captured PNG (800×495, aspect 1.62) layout:
            //   • Continental US fills top ~68% of PNG
            //   • Alaska/Hawaii in lower-left, taking bottom ~32%
            //
            // Using objectFit:'cover' + objectPosition:'top' clips the
            // bottom 32% of the PNG (Alaska/Hawaii) so the visible orange
            // is just continental US at NATURAL aspect — no stretching.
            //
            // Bounds are scaled to OUR magnet (smaller than Zazzle's
            // magnet.jpg frame): magnet at x=22-78 y=15-76, design sits
            // centered on the upper-middle of the magnet face.
            // PIXEL-PERFECT bounds from scripts/find-design-bounds.py:
            // pixel-diffs Zazzle's rendered preview against the empty base.
            // Differing pixels = exact print area Zazzle uses.
            // Bounds match Zazzle's actual US-shape bbox in the captioned and
            // no-label references (detected by scripts/verify-bounds.py).
            designFit: 'contain',
            design: { x: 23.02, y: 33.84, w: 54.00, h: 32.45 },
            designCaptioned: { x: 25.73, y: 32.21, w: 48.69, h: 30.99 },
            caption: {
                x: 50, y: 67.97,
                fontSize: 5.45,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'button',
        name: 'Button',
        zazzleProductId: '256075812325269155',
        // Zazzle has a separate SKU for the design-only button so we route to
        // it when the user opts out of the caption.
        noLabelProductId: '256891852104876723',
        supportsLabel: true,
        imageFile: 'button.jpg',
        noLabelImageFile: 'button-no-label.jpg',
        analyticsKey: 'button',
        accent: '#16a085',
        // PIXEL-PERFECT bounds from scripts/find-design-bounds.py:
        // pixel-diffs Zazzle's rendered preview (gray US placeholder +
        // "Name" caption) against the empty base. The differing pixels
        // ARE the exact print area.
        // `design` is centered on the button (no caption SKU). `designCaptioned`
        // is shifted up to leave room for the "Name" caption underneath.
        // Bounds tuned to the gray US placeholder in button.jpg / button-no-label.jpg.
        // Bounds measured directly from Zazzle's realview render via the
        // screenshot test (tests/screenshots/run.mjs). Both design and
        // designCaptioned use the same bounds shape; the only diff is the
        // y-offset to make room for the "Name" caption below in the
        // captioned variant.
        livePreview: {
            baseFile: 'button-empty.jpg',
            designFit: 'contain',
            design: { x: 25.65, y: 33.13, w: 48.61, h: 33.96 },
            designCaptioned: { x: 25.65, y: 30.14, w: 48.67, h: 33.95 },
            caption: {
                x: 50, y: 68.82,
                fontSize: 6.30,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'keychain',
        name: 'Keychain',
        zazzleProductId: '256392241372801757',
        noLabelProductId: '256905813005175392',
        supportsLabel: true,
        imageFile: 'keychain.jpg',
        noLabelImageFile: 'keychain-no-label.jpg',
        analyticsKey: 'keychain',
        accent: '#c0392b',
        // Round keychain disc with metal ring at top. Empty base has the
        // disc at ~x=22-78, y=33-87 — pushed lower than the round magnet
        // because of the keychain ring. Design sits in the upper portion
        // of the disc, caption near the bottom.
        livePreview: {
            // PIXEL-PERFECT bounds from scripts/find-design-bounds.py.
            baseFile: 'keychain-empty.jpg',
            designFit: 'contain',
            design: { x: 24.46, y: 50.14, w: 51.58, h: 32.55 },
            designCaptioned: { x: 26.81, y: 50.64, w: 46.12, h: 29.34 },
            caption: {
                x: 50, y: 84.51,
                fontSize: 5.40,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'pillow',
        name: 'Pillow',
        // Hidden from gallery + featured strip until we have a proper pillow
        // preview image. The catalog entry stays so existing share links + analytics
        // keep working — just won't show up in the storefront.
        hidden: true,
        zazzleProductId: '256667666916110381',
        supportsLabel: true,
        imageFile: 'pillow.jpg',
        analyticsKey: 'pillow',
        accent: '#8e44ad',
    },
    {
        id: 'stone-coaster',
        name: 'Stone Coaster',
        zazzleProductId: '256770245811618684',
        supportsLabel: true,
        imageFile: 'stone-coaster.jpg',
        analyticsKey: 'stone_coaster',
        accent: '#95a5a6',
        // PIXEL-PERFECT bounds: matched to the US-map region of Zazzle's
        // reference render (corner territory inset boxes that Zazzle adds
        // below aren't part of the user's exported design, so we exclude
        // them from the bounds — including them stretches our preview
        // beyond the actual print area).
        // Bounds measured from Zazzle's realview render via the screenshot test.
        // Sandstone absorbs ink — multiply blend + reduced opacity matches
        // Zazzle's actual print look.
        livePreview: {
            baseFile: 'stone-coaster-empty.jpg',
            designFit: 'contain',
            designBlendMode: 'multiply',
            designOpacity: 0.85,
            design: { x: 10.71, y: 21.10, w: 79.05, h: 55.94 },
            designCaptioned: { x: 10.71, y: 21.10, w: 79.05, h: 55.94 },
            caption: {
                x: 50, y: 85.84,
                fontSize: 10.14,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'beverage-coaster',
        name: 'Beverage Coaster',
        zazzleProductId: '256351739917639985',
        supportsLabel: true,
        imageFile: 'beverage-coaster.jpg',
        analyticsKey: 'beverage_coaster',
        accent: '#d35400',
        // Front-view top-down render fetched from Zazzle's product page.
        // Bounds measured from the realview reference via
        // scripts/find-design-bounds.py + visual tuning. Hardboard prints
        // ink directly so no blend-mode tweaks needed.
        livePreview: {
            baseFile: 'beverage-coaster-empty.jpg',
            designFit: 'contain',
            design: { x: 8.04, y: 17.50, w: 83.98, h: 63.81 },
            designCaptioned: { x: 8.04, y: 17.50, w: 83.98, h: 63.81 },
            caption: {
                x: 50, y: 89.79,
                fontSize: 9.88,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'glass-coaster',
        name: 'Glass Coaster',
        zazzleProductId: '256994849305507950',
        supportsLabel: true,
        imageFile: 'glass-coaster.jpg',
        analyticsKey: 'glass_coaster',
        accent: '#3498db',
        // Front-view top-down render fetched from Zazzle's product page.
        // Bounds match the beverage coaster (same printable area).
        // The frosted glass surface tints the print slightly — multiply
        // blend + minor opacity drop emulates the look so live preview
        // matches Zazzle's render.
        livePreview: {
            baseFile: 'glass-coaster-empty.jpg',
            designFit: 'contain',
            designBlendMode: 'multiply',
            designOpacity: 0.9,
            design: { x: 6.80, y: 15.95, w: 86.31, h: 65.36 },
            designCaptioned: { x: 6.80, y: 15.95, w: 86.31, h: 65.36 },
            caption: {
                x: 50, y: 90.10,
                fontSize: 9.88,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'acrylic-tray',
        name: 'Acrylic Tray',
        zazzleProductId: '256929587582209976',
        supportsLabel: true,
        imageFile: 'acrylic-tray.jpg',
        analyticsKey: 'acrylic_tray',
        accent: '#1abc9c',
        // PIXEL-PERFECT bounds: matched to the US-map region of Zazzle's
        // reference render. Corner territory inset boxes that Zazzle adds
        // below aren't part of the user's exported design.
        // Bounds measured from Zazzle's realview render via the screenshot test.
        // The clear acrylic tray lets the underlying surface show through —
        // multiply blend approximates Zazzle's print look.
        livePreview: {
            baseFile: 'acrylic-tray-empty.jpg',
            designFit: 'contain',
            designBlendMode: 'multiply',
            designOpacity: 0.9,
            design: { x: 15.57, y: 24.41, w: 68.97, h: 48.40 },
            designCaptioned: { x: 15.57, y: 24.41, w: 68.97, h: 48.40 },
            caption: {
                x: 50.05, y: 81.49,
                fontSize: 7.5,
                color: '#000',
                fontWeight: 400,
                maxWidth: 85
            }
        }
    },
    {
        id: 'round-clock',
        name: 'Round Clock',
        // Hidden site-wide until we have a proper wall-clock preview image.
        // Same pattern as pillow: catalog entry stays so SKU + analytics keep
        // working, but it won't render in any gallery.
        hidden: true,
        zazzleProductId: '256360307766560037',
        supportsLabel: true,
        imageFile: 'round-clock.jpg',
        analyticsKey: 'round_clock',
        accent: '#34495e',
    },
    {
        id: 'luggage-tag',
        // Customer-facing name says "Suitcase" because the Zazzle SKU is
        // actually a hard-shell rolling suitcase — the legacy id
        // "luggage-tag" stays so promo SKUs, analytics, and screenshot
        // tests keep working.
        name: 'Suitcase',
        zazzleProductId: '256580976354205821',
        supportsLabel: true,
        imageFile: 'luggage-tag.jpg',
        analyticsKey: 'luggage_tag',
        accent: '#e74c3c',
        // Bounds reflect that this is a rolling suitcase: the design
        // prints small on the front face.
        // Bounds measured from Zazzle's realview render via the screenshot test.
        livePreview: {
            baseFile: 'luggage-tag-empty.jpg',
            designFit: 'contain',
            design: { x: 36.79, y: 31.95, w: 26.79, h: 18.29 },
            designCaptioned: { x: 36.79, y: 31.95, w: 26.79, h: 18.29 },
            caption: {
                x: 50.05, y: 52.14,
                fontSize: 3.35,
                color: '#000',
                fontWeight: 400,
                maxWidth: 50
            }
        }
    },

].map((p) => ({
    ...p,
    image: resolveImage(p.imageFile),
    noLabelImage: p.noLabelImageFile ? resolveImage(p.noLabelImageFile) : undefined,
    livePreview: p.livePreview
        ? {
            ...p.livePreview,
            baseImage: resolveImage(p.livePreview.baseFile)
        }
        : undefined,
}));

export const getProductById = (id) => PRODUCTS.find((p) => p.id === id);
